import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Tooltip from "@mui/material/Tooltip";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import jsPDF from "jspdf";
import Papa from "papaparse";
import { saveAs } from "file-saver";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import logoImage from "assets/images/45icon.png";

import { db } from "firebaseDb";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  setDoc,
  where,
} from "firebase/firestore";

const LEGACY_LOCATION_ID = "all-orchards";
const getStocktakeDocId = (monthKey, locationId) =>
  locationId === LEGACY_LOCATION_ID ? monthKey : `${monthKey}__${locationId}`;

const parseNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const hasValue = (value) =>
  value !== undefined && value !== null && (typeof value !== "string" || value.trim() !== "");

const roundToTwoDecimals = (value) => Math.round(100 * value) / 100;

const calculateChemicalFromSpray = (sprayChemical, spray) => {
  const measure = sprayChemical?.measure;
  const amount = parseNumber(sprayChemical?.sprayRate);
  const hectares = parseNumber(spray?.hectares);
  const waterRate = parseNumber(spray?.waterRate);
  const tankCapacity = parseNumber(spray?.tankCapacity);

  if (measure === "Tank") {
    if (tankCapacity === 0) return 0;
    const tanksperHectare = waterRate / tankCapacity;
    return roundToTwoDecimals(amount * tanksperHectare * hectares);
  }
  if (measure === "Hectare") {
    return roundToTwoDecimals(amount * hectares);
  }
  if (measure === "1000 L") {
    return roundToTwoDecimals((amount * waterRate * hectares) / 1000);
  }
  return 0;
};

const getSprayDate = (rawDate) => {
  if (!rawDate) return null;
  if (typeof rawDate?.toDate === "function") return rawDate.toDate();
  if (rawDate instanceof Date) return rawDate;
  if (typeof rawDate === "string") {
    const parsed = new Date(rawDate);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const buildRowsInputFromSavedRows = (chemicals, savedRows) => {
  const nextRowsInput = {};
  const nextSavedFinalOverrides = {};

  chemicals.forEach((chemical) => {
    const row = savedRows[chemical.key] || {};
    const orders = Array.isArray(row.orders)
      ? row.orders.map((order) => ({
          amount: `${order?.amount ?? ""}`,
          date: order?.date || "",
        }))
      : [];

    nextRowsInput[chemical.key] = {
      initialStock: `${row.initialStock ?? 0}`,
      orders,
      finalAmountOverride: hasValue(row.finalAmountOverride) ? `${row.finalAmountOverride}` : "",
    };

    if (hasValue(row.finalAmountOverride)) {
      nextSavedFinalOverrides[chemical.key] = parseNumber(row.finalAmountOverride);
    }
  });

  return { nextRowsInput, nextSavedFinalOverrides };
};

const findMatchingChemicalKey = (sprayChemical, chemicals) => {
  if (sprayChemical?.key && chemicals.some((chemical) => chemical.key === sprayChemical.key)) {
    return sprayChemical.key;
  }

  const matchingChemical = chemicals.find((chemical) => {
    if (!sprayChemical?.chemicalName || !chemical.chemicalName) return false;
    return (
      sprayChemical.chemicalName.trim().toLowerCase() === chemical.chemicalName.trim().toLowerCase()
    );
  });

  return matchingChemical?.key || "";
};

const calculateUsedByChemical = ({
  sprayDocs,
  chemicals,
  monthStart,
  monthEnd,
  selectedSiteCodes = [],
  useAllSites = false,
}) => {
  const usage = {};
  const usageBreakdown = {};
  const startDate = monthStart.toDate();
  const endDate = monthEnd.toDate();
  const includedSiteCodes = new Set(selectedSiteCodes);

  sprayDocs.forEach((sprayDoc) => {
    const spray = typeof sprayDoc.data === "function" ? sprayDoc.data() : sprayDoc;
    const sprayDate = getSprayDate(spray?.sprayDate);
    if (!sprayDate) return;
    if (sprayDate < startDate || sprayDate >= endDate) return;
    if (!useAllSites && !includedSiteCodes.has(spray?.siteCode)) return;
    if (!Array.isArray(spray?.chemicals)) return;

    spray.chemicals.forEach((sprayChemical, chemicalIndex) => {
      const amountUsed = hasValue(sprayChemical?.actualChemical)
        ? parseNumber(sprayChemical?.actualChemical)
        : calculateChemicalFromSpray(sprayChemical, spray);
      if (amountUsed === 0) return;

      const matchingKey = findMatchingChemicalKey(sprayChemical, chemicals);
      if (!matchingKey) return;

      usage[matchingKey] = (usage[matchingKey] || 0) + amountUsed;
      if (!usageBreakdown[matchingKey]) {
        usageBreakdown[matchingKey] = [];
      }
      usageBreakdown[matchingKey].push({
        detailId: `${
          sprayDoc.id || spray?.sprayInstructionId || "spray"
        }-${matchingKey}-${chemicalIndex}`,
        sprayDate: dayjs(sprayDate).format("YYYY-MM-DD"),
        siteName: spray?.siteName || spray?.siteCode || "Unknown Orchard",
        amountUsed: roundToTwoDecimals(amountUsed),
      });
    });
  });

  return { usage, usageBreakdown };
};

const calculateFinalAmountFromRow = (row, usedAmount) => {
  if (hasValue(row.finalAmountOverride)) {
    return parseNumber(row.finalAmountOverride);
  }

  const orders = Array.isArray(row.orders) ? row.orders : [];
  const ordersTotal = orders.reduce((sum, order) => sum + parseNumber(order.amount), 0);
  return parseNumber(row.initialStock) + ordersTotal - usedAmount;
};

function Stocktake() {
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [chemicals, setChemicals] = useState([]);
  const [stockLocations, setStockLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState(LEGACY_LOCATION_ID);
  const [rowsInput, setRowsInput] = useState({});
  const [usedByChemical, setUsedByChemical] = useState({});
  const [usedBreakdownByChemical, setUsedBreakdownByChemical] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importingPrevious, setImportingPrevious] = useState(false);
  const [savedFinalOverrides, setSavedFinalOverrides] = useState({});

  const selectedLocation = useMemo(() => {
    if (selectedLocationId === LEGACY_LOCATION_ID) {
      return {
        key: LEGACY_LOCATION_ID,
        name: "All Orchards (Legacy)",
        orchardSiteCodes: [],
      };
    }
    return (
      stockLocations.find((location) => location.key === selectedLocationId) || {
        key: LEGACY_LOCATION_ID,
        name: "All Orchards (Legacy)",
        orchardSiteCodes: [],
      }
    );
  }, [selectedLocationId, stockLocations]);

  useEffect(() => {
    if (
      selectedLocationId !== LEGACY_LOCATION_ID &&
      !stockLocations.some((location) => location.key === selectedLocationId)
    ) {
      setSelectedLocationId(LEGACY_LOCATION_ID);
    }
  }, [selectedLocationId, stockLocations]);

  useEffect(() => {
    let active = true;
    const loadInitialData = async () => {
      try {
        const chemRef = collection(db, "Chemicals");
        const chemQuery = query(chemRef, orderBy("chemicalName", "asc"));
        const locationsQuery = query(collection(db, "StocktakeLocations"), orderBy("name", "asc"));
        const [snapshot, locationsSnapshot] = await Promise.all([
          getDocs(chemQuery),
          getDocs(locationsQuery),
        ]);
        if (!active) return;
        const list = [];
        snapshot.forEach((docu) => {
          if (!docu.data()?.archive) {
            list.push({ key: docu.id, ...docu.data() });
          }
        });
        const locationList = locationsSnapshot.docs.map((locationDoc) => ({
          key: locationDoc.id,
          ...locationDoc.data(),
          orchardSiteCodes: Array.isArray(locationDoc.data()?.orchardSiteCodes)
            ? locationDoc.data().orchardSiteCodes
            : [],
        }));
        setChemicals(list);
        setStockLocations(locationList);
      } catch (error) {
        console.error("Failed loading stocktake setup data", error);
      }
    };

    loadInitialData();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!chemicals.length) {
      setRowsInput({});
      setUsedByChemical({});
      setUsedBreakdownByChemical({});
      setSavedFinalOverrides({});
      setLoading(false);
      return;
    }

    let active = true;

    const loadMonthData = async () => {
      setLoading(true);
      try {
        const monthStart = selectedMonth.startOf("month");
        const monthEnd = monthStart.add(1, "month");
        const monthKey = monthStart.format("YYYY-MM");
        const stocktakeDocId = getStocktakeDocId(monthKey, selectedLocation.key);

        const stocktakeRef = doc(db, "Stocktake", stocktakeDocId);
        const [stocktakeSnap, spraySnap] = await Promise.all([
          getDoc(stocktakeRef),
          getDocs(
            query(
              collection(db, "Sprays"),
              where("sprayDate", ">=", monthStart.toDate()),
              where("sprayDate", "<", monthEnd.toDate())
            )
          ),
        ]);

        if (!active) return;

        const savedRows = stocktakeSnap.exists() ? stocktakeSnap.data()?.rows || {} : {};
        const { nextRowsInput, nextSavedFinalOverrides } = buildRowsInputFromSavedRows(
          chemicals,
          savedRows
        );
        const { usage, usageBreakdown } = calculateUsedByChemical({
          sprayDocs: spraySnap.docs,
          chemicals,
          monthStart,
          monthEnd,
          selectedSiteCodes: selectedLocation.orchardSiteCodes,
          useAllSites: selectedLocation.key === LEGACY_LOCATION_ID,
        });

        setRowsInput(nextRowsInput);
        setUsedByChemical(usage);
        setUsedBreakdownByChemical(usageBreakdown);
        setSavedFinalOverrides(nextSavedFinalOverrides);
      } catch (error) {
        console.error("Failed loading stocktake month data", error);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadMonthData();

    // eslint-disable-next-line consistent-return
    return () => {
      active = false;
    };
  }, [chemicals, selectedLocation, selectedMonth]);

  const tableRows = useMemo(
    () =>
      chemicals.map((chemical) => {
        const input = rowsInput[chemical.key] || {
          initialStock: "0",
          orders: [],
          finalAmountOverride: "",
        };
        const initialStock = parseNumber(input.initialStock);
        const orders = Array.isArray(input.orders) ? input.orders : [];
        const ordersTotal = orders.reduce((sum, order) => sum + parseNumber(order.amount), 0);
        const used = Number(usedByChemical[chemical.key] || 0);
        const usedDetails = usedBreakdownByChemical[chemical.key] || [];
        const calculatedFinalAmount = Number(initialStock + ordersTotal - used);
        const hasFinalAmountOverride = hasValue(input.finalAmountOverride);
        const overrideFinalAmount = hasFinalAmountOverride
          ? parseNumber(input.finalAmountOverride)
          : null;
        const finalAmount = hasFinalAmountOverride ? overrideFinalAmount : calculatedFinalAmount;
        const savedOverride = savedFinalOverrides[chemical.key];

        return {
          key: chemical.key,
          chemicalName: chemical.chemicalName,
          unit: chemical.unit || "",
          initialStock,
          initialStockInput: input.initialStock,
          orders,
          ordersTotal,
          used,
          usedDetails,
          calculatedFinalAmount,
          finalAmountOverrideInput: input.finalAmountOverride,
          hasFinalAmountOverride,
          differenceFromCalculated: finalAmount - calculatedFinalAmount,
          showOverrideDetails:
            hasFinalAmountOverride && savedOverride === parseNumber(input.finalAmountOverride),
          finalAmount,
        };
      }),
    [chemicals, rowsInput, savedFinalOverrides, usedBreakdownByChemical, usedByChemical]
  );

  const exportRows = useMemo(
    () =>
      tableRows.filter(
        (row) =>
          row.initialStock !== 0 || row.ordersTotal !== 0 || row.used !== 0 || row.finalAmount !== 0
      ),
    [tableRows]
  );

  const updateInitialStock = (chemicalKey, value) => {
    setRowsInput((prev) => ({
      ...prev,
      [chemicalKey]: {
        initialStock: value,
        orders: prev[chemicalKey]?.orders || [],
        finalAmountOverride: prev[chemicalKey]?.finalAmountOverride || "",
      },
    }));
  };

  const updateFinalAmountOverride = (chemicalKey, value) => {
    setRowsInput((prev) => {
      const existing = prev[chemicalKey] || {
        initialStock: "0",
        orders: [],
        finalAmountOverride: "",
      };
      return {
        ...prev,
        [chemicalKey]: {
          ...existing,
          finalAmountOverride: value,
        },
      };
    });
  };

  const addOrder = (chemicalKey) => {
    setRowsInput((prev) => {
      const existing = prev[chemicalKey] || { initialStock: "0", orders: [] };
      return {
        ...prev,
        [chemicalKey]: {
          ...existing,
          orders: [
            ...existing.orders,
            { amount: "", date: selectedMonth.startOf("month").format("YYYY-MM-DD") },
          ],
        },
      };
    });
  };

  const removeOrder = (chemicalKey, orderIndex) => {
    setRowsInput((prev) => {
      const existing = prev[chemicalKey] || { initialStock: "0", orders: [] };
      return {
        ...prev,
        [chemicalKey]: {
          ...existing,
          orders: existing.orders.filter((_, index) => index !== orderIndex),
        },
      };
    });
  };

  const updateOrder = (chemicalKey, orderIndex, field, value) => {
    setRowsInput((prev) => {
      const existing = prev[chemicalKey] || { initialStock: "0", orders: [] };
      const nextOrders = [...existing.orders];
      nextOrders[orderIndex] = {
        ...nextOrders[orderIndex],
        [field]: value,
      };
      return {
        ...prev,
        [chemicalKey]: {
          ...existing,
          orders: nextOrders,
        },
      };
    });
  };

  const saveStocktake = async () => {
    setSaving(true);
    try {
      const monthKey = selectedMonth.startOf("month").format("YYYY-MM");
      const stocktakeDocId = getStocktakeDocId(monthKey, selectedLocation.key);

      const rows = {};
      tableRows.forEach((row) => {
        const cleanedOrders = row.orders
          .map((order) => ({ amount: parseNumber(order.amount), date: order.date || "" }))
          .filter((order) => order.amount !== 0 || order.date);

        rows[row.key] = {
          initialStock: row.initialStock,
          orders: cleanedOrders,
        };

        if (row.hasFinalAmountOverride) {
          rows[row.key].finalAmountOverride = parseNumber(row.finalAmountOverrideInput);
        }
      });

      await setDoc(
        doc(db, "Stocktake", stocktakeDocId),
        {
          monthKey,
          monthLabel: selectedMonth.format("MMMM YYYY"),
          locationId: selectedLocation.key,
          locationName: selectedLocation.name,
          rows,
          updatedAt: new Date(),
        },
        { merge: true }
      );

      const nextSavedFinalOverrides = {};
      tableRows.forEach((row) => {
        if (row.hasFinalAmountOverride) {
          nextSavedFinalOverrides[row.key] = parseNumber(row.finalAmountOverrideInput);
        }
      });
      setSavedFinalOverrides(nextSavedFinalOverrides);
      alert("Stocktake saved.");
    } catch (error) {
      console.error("Failed saving stocktake", error);
      alert("Failed to save stocktake.");
    } finally {
      setSaving(false);
    }
  };

  const importInitialStockFromPreviousMonth = async () => {
    setImportingPrevious(true);
    try {
      const currentMonth = selectedMonth.startOf("month");
      const previousMonth = selectedMonth.startOf("month").subtract(1, "month");
      const currentMonthKey = currentMonth.format("YYYY-MM");
      const previousMonthKey = previousMonth.format("YYYY-MM");
      const currentDocId = getStocktakeDocId(currentMonthKey, selectedLocation.key);
      const previousDocId = getStocktakeDocId(previousMonthKey, selectedLocation.key);
      const previousMonthEnd = previousMonth.add(1, "month");
      const [previousMonthSnap, currentMonthSnap, previousMonthSpraySnap] = await Promise.all([
        getDoc(doc(db, "Stocktake", previousDocId)),
        getDoc(doc(db, "Stocktake", currentDocId)),
        getDocs(
          query(
            collection(db, "Sprays"),
            where("sprayDate", ">=", previousMonth.toDate()),
            where("sprayDate", "<", previousMonthEnd.toDate())
          )
        ),
      ]);

      if (!previousMonthSnap.exists()) {
        alert(`No stocktake saved for ${previousMonth.format("MMMM YYYY")}.`);
        return;
      }

      const previousRows = previousMonthSnap.data()?.rows || {};
      const currentRows = currentMonthSnap.exists() ? currentMonthSnap.data()?.rows || {} : {};
      const { usage: previousUsage } = calculateUsedByChemical({
        sprayDocs: previousMonthSpraySnap.docs,
        chemicals,
        monthStart: previousMonth,
        monthEnd: previousMonthEnd,
        selectedSiteCodes: selectedLocation.orchardSiteCodes,
        useAllSites: selectedLocation.key === LEGACY_LOCATION_ID,
      });
      const importedInitialStocks = {};

      chemicals.forEach((chemical) => {
        const previousRow = previousRows[chemical.key] || {};
        importedInitialStocks[chemical.key] = calculateFinalAmountFromRow(
          previousRow,
          Number(previousUsage[chemical.key] || 0)
        );
      });

      setRowsInput((prev) => {
        const next = { ...prev };
        chemicals.forEach((chemical) => {
          const existing = prev[chemical.key] || {
            initialStock: "0",
            orders: [],
            finalAmountOverride: "",
          };
          next[chemical.key] = {
            ...existing,
            initialStock: `${roundToTwoDecimals(importedInitialStocks[chemical.key])}`,
          };
        });
        return next;
      });

      const nextRows = { ...currentRows };
      chemicals.forEach((chemical) => {
        nextRows[chemical.key] = {
          ...(currentRows[chemical.key] || {}),
          initialStock: roundToTwoDecimals(importedInitialStocks[chemical.key]),
        };
      });

      await setDoc(
        doc(db, "Stocktake", currentDocId),
        {
          monthKey: currentMonthKey,
          monthLabel: currentMonth.format("MMMM YYYY"),
          locationId: selectedLocation.key,
          locationName: selectedLocation.name,
          rows: nextRows,
          updatedAt: new Date(),
        },
        { merge: true }
      );

      alert(
        `Initial stock imported from ${previousMonth.format("MMMM YYYY")} final amounts and saved.`
      );
    } catch (error) {
      console.error("Failed importing previous month stocktake", error);
      alert("Failed to import previous month initial stock.");
    } finally {
      setImportingPrevious(false);
    }
  };

  const loadImage = (src) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
      img.src = src;
    });

  const createPdf = async () => {
    if (!exportRows.length) {
      alert("No non-zero stocktake rows to export.");
      return;
    }
    let logo = null;
    try {
      logo = await loadImage(logoImage);
    } catch (error) {
      console.error("Stocktake PDF logo failed to load", error);
    }
    // eslint-disable-next-line new-cap
    const pdf = new jsPDF({ unit: "pt", format: "a4", orientation: "landscape" });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 30;
    const contentWidth = pageWidth - margin * 2;

    const colorDarkBlue = [13, 71, 133];
    const colorLightGray = [230, 230, 230];
    const colorWhite = [255, 255, 255];
    const colorLightRed = [255, 210, 210];
    const colorBorder = [210, 210, 210];
    const colorText = [50, 50, 50];
    const colorMuted = [150, 150, 150];
    const colorLinkBlue = [30, 100, 200];

    const cellPad = 5;
    const rowLineH = 13;
    const minRowH = 22;
    const sectionH = 20;
    const colHeadH = 18;

    // Column definitions — last column fills remaining width
    const fixedCols = [
      { label: "CHEM. NAME", width: 150 },
      { label: "INITIAL STOCK", width: 65 },
      { label: "UNITS", width: 58 },
      { label: "ORDERS", width: 165 },
      { label: "USED AMOUNT", width: 68 },
      { label: "FINAL AMOUNT", width: 78 },
      { label: "CHECKED BY", width: 75 },
    ];
    const fixedTotal = fixedCols.reduce((s, c) => s + c.width, 0);
    const cols = [...fixedCols, { label: "NOTES", width: contentWidth - fixedTotal }];

    // Helper: draw a horizontal rule
    const hline = (xFrom, xTo, yPos, lw, color) => {
      pdf.setDrawColor(...color);
      pdf.setLineWidth(lw);
      pdf.line(xFrom, yPos, xTo, yPos);
    };

    let y = margin;

    // ── Title ──────────────────────────────────────────────
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(22);
    pdf.setTextColor(...colorDarkBlue);
    pdf.text("Monthly Stocktake Report", margin, y + 20);
    pdf.setFontSize(14);
    pdf.setTextColor(...colorText);
    pdf.text("45 South Management Ltd.", margin + 500, y + 20);
    y += 28;
    hline(margin, pageWidth - margin, y, 2, colorDarkBlue);

    if (logo) {
      pdf.addImage(logo, "PNG", margin + 720, y - 40, 55, 40);
    }
    y += 14;
    // ── Company / Date meta ────────────────────────────────
    const metaLabelX = margin;
    const metaValueX = margin + 95;
    const metaRowH = 14;

    pdf.setFontSize(12);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...colorMuted);
    pdf.text("DATE", metaLabelX, y + 9);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(...colorText);
    pdf.setFontSize(18);
    pdf.text(selectedMonth.format("MMMM YYYY"), metaValueX, y + 9);
    y += metaRowH + 14;

    // ── Section header bar ─────────────────────────────────
    pdf.setFillColor(...colorDarkBlue);
    pdf.rect(margin, y, contentWidth, sectionH, "F");
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    pdf.setTextColor(...colorWhite);
    pdf.text("CHEMICAL STOCK DETAILS", pageWidth / 2, y + sectionH / 2 + 3, { align: "center" });
    y += sectionH;

    // ── Column headers ─────────────────────────────────────
    pdf.setFillColor(...colorLightGray);
    pdf.rect(margin, y, contentWidth, colHeadH, "F");
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7);
    pdf.setTextColor(80, 80, 80);
    let xCur = margin;
    cols.forEach((col) => {
      pdf.text(col.label, xCur + cellPad, y + colHeadH / 2 + 3);
      xCur += col.width;
    });
    hline(margin, pageWidth - margin, y + colHeadH, 0.5, colorBorder);
    y += colHeadH;

    // ── Data rows ──────────────────────────────────────────
    exportRows.forEach((row, rowIdx) => {
      const orderLines =
        row.orders.length > 0
          ? row.orders.map((order) => {
              const d = order.date ? dayjs(order.date).format("M/D/YY") : "";
              const amt = order.amount !== "" ? order.amount : "0";
              const u = row.unit ? `${row.unit}s` : "";
              const line = d ? `${d} - ${amt} ${u}` : `${amt} ${u}`;
              return line.trim();
            })
          : [""];

      const rowH = Math.max(minRowH, orderLines.length * rowLineH + cellPad * 2);

      if (y + rowH > pageHeight - margin) {
        pdf.addPage();
        y = margin;

        // Repeat section + column headers on each new page
        pdf.setFillColor(...colorDarkBlue);
        pdf.rect(margin, y, contentWidth, sectionH, "F");
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(9);
        pdf.setTextColor(...colorWhite);
        pdf.text("CHEMICAL STOCK DETAILS", pageWidth / 2, y + sectionH / 2 + 3, {
          align: "center",
        });
        y += sectionH;

        pdf.setFillColor(...colorLightGray);
        pdf.rect(margin, y, contentWidth, colHeadH, "F");
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(7);
        pdf.setTextColor(80, 80, 80);

        xCur = margin;

        cols.forEach((col) => {
          pdf.text(col.label, xCur + cellPad, y + colHeadH / 2 + 3);
          xCur += col.width;
        });

        hline(margin, pageWidth - margin, y + colHeadH, 0.5, colorBorder);

        y += colHeadH;
      }

      // Alternating row background
      if (rowIdx % 2 === 1) {
        pdf.setFillColor(248, 248, 250);
        pdf.rect(margin, y, contentWidth, rowH, "F");
      }

      const midY = y + rowH / 2 + 3;
      xCur = margin;
      pdf.setFontSize(8);

      // CHEM. NAME
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(...colorText);
      pdf.text(row.chemicalName || "", xCur + cellPad, midY);
      xCur += cols[0].width;

      // INITIAL STOCK
      pdf.text(`${row.initialStock}`, xCur + cellPad, midY);
      xCur += cols[1].width;

      // UNITS
      pdf.text(row.unit ? `${row.unit}s` : "", xCur + cellPad, midY);
      xCur += cols[2].width;

      // ORDERS (one line per order, blue)
      pdf.setTextColor(...colorLinkBlue);

      orderLines.forEach((orderLine, idx) => {
        const lineY = y + cellPad + (idx + 1) * rowLineH;
        pdf.text(orderLine, xCur + cellPad, lineY);
      });

      pdf.setTextColor(...colorText);
      xCur += cols[3].width;

      // USED AMOUNT (blue)
      pdf.setTextColor(...colorLinkBlue);
      pdf.text(`${row.used.toFixed(2)}`, xCur + cellPad, midY);
      pdf.setTextColor(...colorText);
      xCur += cols[4].width;

      // FINAL AMOUNT (light-red background if negative)
      if (row.finalAmount < 0) {
        pdf.setFillColor(...colorLightRed);
        pdf.rect(xCur + 1, y + 1, cols[5].width - 2, rowH - 2, "F");
        pdf.setTextColor(180, 0, 0);
      }

      pdf.text(`${row.finalAmount.toFixed(2)}`, xCur + cellPad, midY);

      pdf.setTextColor(...colorText);
      xCur += cols[5].width;

      // CHECKED BY (blank)
      xCur += cols[6].width;

      // NOTES (blank)
      // xCur += cols[7].width;

      // Row bottom border
      hline(margin, pageWidth - margin, y + rowH, 0.5, colorBorder);

      y += rowH;
    });

    pdf.save(`stocktake-${selectedMonth.format("YYYY-MM")}.pdf`);
  };

  const createCsv = () => {
    if (!exportRows.length) {
      alert("No non-zero stocktake rows to export.");
      return;
    }

    const csvRows = exportRows.map((row) => ({
      chemical: row.chemicalName,
      unit: row.unit,
      initialStock: row.initialStock,
      ordersTotal: row.ordersTotal,
      usedAmount: row.used,
      finalAmount: row.finalAmount,
      orders: row.orders
        .map((order) => `${parseNumber(order.amount)} on ${order.date || "No date"}`)
        .join(" | "),
    }));

    const csv = Papa.unparse(csvRows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `stocktake-${selectedMonth.format("YYYY-MM")}.csv`);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar absolute isMini />
      <MDBox mt={10} mb={3}>
        <Card>
          <MDBox p={3}>
            <MDTypography variant="h5" mb={2}>
              Stocktake
            </MDTypography>
            <Grid container spacing={2} alignItems="center" mb={2}>
              <Grid item xs={12} md={4}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Select Month"
                    views={["year", "month"]}
                    value={selectedMonth}
                    onChange={(newValue) => {
                      if (newValue) {
                        setSelectedMonth(newValue);
                      }
                    }}
                    inputFormat="MMMM YYYY"
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={2} md={2}>
                <FormControl fullWidth>
                  <InputLabel id="stock-location-label">Stock Location</InputLabel>
                  <Select
                    labelId="stock-location-label"
                    value={selectedLocationId}
                    label="Stock Location"
                    onChange={(event) => setSelectedLocationId(event.target.value)}
                    sx={{
                      height: 45,
                    }}
                  >
                    <MenuItem value={LEGACY_LOCATION_ID}>All Orchards (Legacy)</MenuItem>

                    {stockLocations.map((location) => (
                      <MenuItem key={location.key} value={location.key}>
                        {location.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={8} md={6}>
                <MDBox display="flex" justifyContent={{ xs: "flex-start", md: "flex-end" }} gap={1}>
                  <MDButton
                    variant="gradient"
                    color="success"
                    onClick={saveStocktake}
                    disabled={saving}
                  >
                    <Icon>save</Icon>&nbsp;{saving ? "Saving..." : "Save Stocktake"}
                  </MDButton>
                  <MDButton
                    variant="outlined"
                    color="secondary"
                    onClick={importInitialStockFromPreviousMonth}
                    disabled={importingPrevious || saving || loading}
                  >
                    <Icon>content_paste</Icon>&nbsp;
                    {importingPrevious ? "Importing..." : "Import Previous Month Initial Stock"}
                  </MDButton>
                  <MDButton variant="outlined" color="info" onClick={createPdf}>
                    <Icon>picture_as_pdf</Icon>&nbsp;Export PDF
                  </MDButton>
                  <MDButton variant="outlined" color="dark" onClick={createCsv}>
                    <Icon>download</Icon>&nbsp;Export CSV
                  </MDButton>
                </MDBox>
              </Grid>
              <Grid item xs={12}>
                <MDTypography variant="caption" color="text">
                  Counting sprays from:{" "}
                  {selectedLocationId === LEGACY_LOCATION_ID
                    ? "all orchards"
                    : (selectedLocation.orchardSiteCodes || []).join(", ")}
                </MDTypography>
              </Grid>
            </Grid>

            {loading ? (
              <MDBox textAlign="center" py={6}>
                <CircularProgress />
              </MDBox>
            ) : (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead sx={{ display: "table-header-group", padding: 0, borderRadius: 0 }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>Chemical</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Initial Stock</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Units</TableCell>
                      <TableCell sx={{ fontWeight: "bold", minWidth: 340 }}>Orders</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Used Amount</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Final Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableRows.map((row) => (
                      <TableRow
                        key={row.key}
                        hover
                        sx={{
                          backgroundColor:
                            // eslint-disable-next-line no-nested-ternary
                            row.finalAmount === 0
                              ? "transparent"
                              : row.finalAmount > 0
                              ? "#e5f3f7"
                              : "#ffebee",
                        }}
                      >
                        <TableCell>{row.chemicalName}</TableCell>
                        <TableCell sx={{ width: 170 }}>
                          <TextField
                            size="small"
                            type="number"
                            value={row.initialStockInput}
                            onChange={(event) => updateInitialStock(row.key, event.target.value)}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell>{row.unit}s</TableCell>
                        <TableCell>
                          <MDBox display="flex" flexDirection="column" gap={1}>
                            {row.orders.map((order, orderIndex) => (
                              // eslint-disable-next-line react/no-array-index-key
                              <MDBox key={`${row.key}-order-${orderIndex}`} display="flex" gap={1}>
                                <TextField
                                  size="small"
                                  label="Amount"
                                  type="number"
                                  value={order.amount}
                                  onChange={(event) =>
                                    updateOrder(row.key, orderIndex, "amount", event.target.value)
                                  }
                                  sx={{ width: 110 }}
                                />
                                <TextField
                                  size="small"
                                  label="Date"
                                  type="date"
                                  value={order.date}
                                  onChange={(event) =>
                                    updateOrder(row.key, orderIndex, "date", event.target.value)
                                  }
                                  InputLabelProps={{ shrink: true }}
                                  sx={{ width: 160 }}
                                />
                                <MDButton
                                  iconOnly
                                  color="error"
                                  size="small"
                                  onClick={() => removeOrder(row.key, orderIndex)}
                                >
                                  <Icon>delete</Icon>
                                </MDButton>
                              </MDBox>
                            ))}
                            <MDButton variant="text" color="info" onClick={() => addOrder(row.key)}>
                              <Icon>add</Icon>&nbsp;Add Order
                            </MDButton>
                          </MDBox>
                        </TableCell>
                        <TableCell>
                          {row.usedDetails.length > 0 ? (
                            <Tooltip
                              arrow
                              placement="top"
                              title={
                                <MDBox py={0.5}>
                                  {row.usedDetails.map((detail) => (
                                    <MDTypography
                                      key={`${row.key}-${detail.detailId}`}
                                      variant="caption"
                                      color="white"
                                      display="block"
                                    >
                                      {`${detail.sprayDate} • ${detail.siteName} • ${detail.amountUsed}`}{" "}
                                      {row.unit || ""}
                                    </MDTypography>
                                  ))}
                                </MDBox>
                              }
                            >
                              <MDBox
                                component="span"
                                tabIndex={0}
                                sx={{ cursor: "help", textDecoration: "underline" }}
                              >
                                {row.used.toFixed(2)}
                              </MDBox>
                            </Tooltip>
                          ) : (
                            row.used.toFixed(2)
                          )}
                        </TableCell>
                        <TableCell sx={{ width: 230 }}>
                          <TextField
                            size="small"
                            type="number"
                            value={row.finalAmountOverrideInput}
                            onChange={(event) =>
                              updateFinalAmountOverride(row.key, event.target.value)
                            }
                            placeholder={row.calculatedFinalAmount.toFixed(2)}
                            fullWidth
                          />
                          {row.showOverrideDetails && (
                            <MDBox mt={0.5}>
                              <MDTypography variant="caption" color="text" display="block">
                                Calculated: {row.calculatedFinalAmount.toFixed(2)}
                              </MDTypography>
                              <MDTypography variant="caption" color="text" display="block">
                                Difference: {row.differenceFromCalculated.toFixed(2)}
                              </MDTypography>
                            </MDBox>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </MDBox>
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Stocktake;
